import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import styles from './UserLookup.module.css';
import useUserLookup from './useUserLookup';
import { IUserDto } from '../../../utils/constants/interfaces';
import { UserLookupDisplayMode } from '../../../utils/constants/enums';
import { Permissions } from '../../../utils/constants/permissions';
import Container from '../../ui/Container/Container';
import { UserQuickAdd } from '../UserQuickAdd/UserQuickAdd';

// Permission display labels matching UserEditor labels
const permissionLabels: Record<string, string> = {
  [Permissions.SYSADMIN]: 'System Administrator',
  [Permissions.MEMBER_UPDATE]: 'Update Members',
  [Permissions.MEMBER_READ_ONLY]: 'Read-only Access',
  [Permissions.MEMBER_DELETE]: 'Delete Members',
  [Permissions.WORKHOURS_UPDATE]: 'Update Work Hours',
  [Permissions.GEN_INVOICE]: 'Generate Invoices'
};

interface UserLookupProps {
  userId?: string;
  visible?: boolean;
  modalView?: boolean;
  displayMode?: UserLookupDisplayMode;
  showInactiveFilter?: boolean;
  requireKeywords?: boolean; //should we only search if keywords are provided?
  onChange: (userId: string) => void;
}

export const UserLookup = ({
  userId = '',
  visible = true,
  modalView = false,
  displayMode = UserLookupDisplayMode.DEFAULT,
  requireKeywords = false,
  onChange
}: UserLookupProps) => {
  const manager = useUserLookup(userId, requireKeywords, onChange);

  if (!visible) return <></>;

  return (
    <>
      <Container visible={manager.showUserQuickAddForm}>
        <UserQuickAdd
          onCancel={() => manager.setShowUserQuickAddForm(false)}
          onUserAdded={onChange}
        />
      </Container>
      <Container visible={!manager.showUserQuickAddForm} className={styles.lookupSearchContainer}>
        <div className={styles.lookupSearchContainerFilters}>
          <div className={styles.lookupSearchHeader}>
            <div className="form-group pe-3">
              <label htmlFor="keywordSearch">User Search</label>
              <input
                id="keywordSearch"
                type="text"
                className="form-control"
                placeholder=""
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  manager.debouncedUserSearch(
                    e.currentTarget.id.toLowerCase(),
                    e.currentTarget.value
                  )
                }
              />
            </div>
            <Container visible={displayMode === UserLookupDisplayMode.SIMPLE} className="ps-2 pb-1">
              <a
                href="#"
                className="btn btn-primary btn-sm"
                onClick={(e) => {
                  e.preventDefault();
                  manager.setShowUserQuickAddForm(true);
                }}>
                Add New User
              </a>
            </Container>
          </div>
          <div className="mt-3">
            <label className="form-label fw-bold">Filter by Permissions</label>
            <div className="d-flex flex-column gap-2">
              {Object.values(Permissions)
                .filter((permission) => permission !== Permissions.APP_USER)
                .map((permission) => (
                  <div key={permission} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`permission-${permission}`}
                      checked={manager.selectedPermissions.includes(permission)}
                      onChange={(e) => manager.handlePermissionChange(permission, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`permission-${permission}`}>
                      {permissionLabels[permission]}
                    </label>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div className={styles.lookupSearchResultContainer}>
          <LoadingContainer isLoading={manager.loading}>
            <Container
              visible={manager.hasSearchParameters && manager.userPagedResults.resultCount === 0}>
              <div>No users found.</div>
            </Container>
            <Container visible={manager.userPagedResults.resultCount > 0}>
              <>
                <Container visible={displayMode === UserLookupDisplayMode.DEFAULT}>
                  <div
                    className={
                      modalView ? styles.lookupSearchResultsModal : styles.lookupSearchResults
                    }>
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th onClick={() => manager.changeSort('firstname')} scope="col">
                            First Name
                          </th>
                          <th onClick={() => manager.changeSort('lastname')} scope="col">
                            Last Name
                          </th>
                          <th onClick={() => manager.changeSort('email')} scope="col">
                            Email Address
                          </th>
                          <th onClick={() => manager.changeSort('phonenumber')} scope="col">
                            Cell Phone
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {manager.userPagedResults.results?.map((user: IUserDto) => {
                          return (
                            <tr
                              key={user.id}
                              className={styles.clickableRow}
                              onClick={(e) => {
                                e.preventDefault();
                                manager.selectUser(user.id ?? '');
                              }}>
                              <td>{user.firstName}</td>
                              <td>{user.lastName}</td>
                              <td>{user.emailAddress}</td>
                              <td>{user.cellPhone}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Container>
                <Container
                  visible={displayMode === UserLookupDisplayMode.SIMPLE}
                  className={
                    modalView ? styles.lookupSearchResultsModal : styles.lookupSearchResults
                  }>
                  {manager.userPagedResults.results?.map((user: IUserDto) => {
                    return (
                      <div key={user.id} className={styles.lookupSearchResultSimple}>
                        <div>
                          <div>
                            {user.lastName}, {user.firstName}
                          </div>
                          <div className={styles.userDetails}>{user.emailAddress}</div>
                        </div>
                        <div>
                          <a
                            href="#"
                            className="btn btn-primary btn-sm me-2"
                            onClick={(e) => {
                              e.preventDefault();
                              manager.selectUser(user.id ?? '');
                            }}>
                            Select
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </Container>
                <div className="pt-4">
                  <PagingControls
                    page={manager.userPagedResults.currentPage ?? 1}
                    pageCount={manager.userPagedResults.pageCount ?? 1}
                    maxPlusOrMinusPagesToShow={3}
                    onPageChange={manager.changePage}
                  />
                </div>
              </>
            </Container>
          </LoadingContainer>
        </div>
      </Container>
    </>
  );
};
